/**
 * Split bundle cart lines into tax basis amounts (book 5% vs digital/CD 14% in NS).
 * Used when bundle_options include taxAllocations per fixed-price tier.
 */

import { getLineUnitPriceCents } from './cartPricing';
import { classifyTaxCategory } from './taxCategories';

function normalizeProductId(value) {
  const n = parseInt(String(value), 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Build tax allocations for a customer-assembled bundle by spreading the
 * discounted bundle price across its components in proportion to list price.
 * Fixed packages carry admin-authored allocations instead; without either, a
 * mixed bundle would be taxed entirely at the parent product's rate.
 *
 * Cents are distributed so the allocations always sum to exactly totalCents.
 */
export function buildBundleTaxAllocations(components, totalCents) {
  const parts = (components || []).filter(Boolean);
  const total = Math.max(0, Math.round(Number(totalCents) || 0));
  if (parts.length === 0 || total === 0) return null;

  const basis = parts.map((part) => Math.max(0, getLineUnitPriceCents(part)));
  const basisTotal = basis.reduce((sum, cents) => sum + cents, 0);

  // Without list prices to weight by, split evenly rather than dropping the split.
  const weights = basisTotal > 0 ? basis : parts.map(() => 1);
  const weightTotal = weights.reduce((sum, w) => sum + w, 0);

  let allocated = 0;
  const rows = parts.map((part, idx) => {
    const isLast = idx === parts.length - 1;
    const amountCents = isLast
      ? total - allocated
      : Math.round((total * weights[idx]) / weightTotal);
    allocated += amountCents;

    const taxCategory = classifyTaxCategory(part);
    return {
      productId: part.id,
      amountCents,
      taxCategory,
      ...(taxCategory === 'custom' ? { taxRate: part.tax_rate } : {}),
    };
  });

  return rows.filter((row) => row.amountCents > 0);
}

/**
 * Expand cart items for tax: bundles with bundleTaxAllocations become synthetic
 * lines with allocated amounts; other lines pass through unchanged.
 */
export function expandItemsForTaxCalculation(cartItems) {
  const expanded = [];

  for (const item of cartItems || []) {
    const qty = item.quantity || 1;
    const allocations = item.bundleTaxAllocations;

    if (
      item.isBundle &&
      Array.isArray(allocations) &&
      allocations.length > 0
    ) {
      allocations.forEach((row) => {
        const amountCents = Number(row.amountCents) || 0;
        if (amountCents <= 0) return;

        const productId = normalizeProductId(row.productId);
        const component = (item.bundleProducts || []).find(
          (p) => p.id === productId || String(p.id) === String(row.productId)
        );
        const label = component?.name || item.name || 'Bundle item';
        const taxCategory = String(row.taxCategory || 'standard').toLowerCase();

        // Tax treatment is stated outright rather than re-inferred, so a CD whose
        // title contains a rebate keyword ("...Collection") is not read as a book.
        const TAX_TYPE_BY_CATEGORY = {
          book: 'gst',
          exempt: 'exempt',
          custom: 'custom',
        };

        const synthetic = {
          name: label,
          quantity: qty,
          unitPrice: amountCents,
          category: taxCategory === 'book' ? 'books' : (component?.category || item.category),
          type: component?.type || item.type,
          tax_type: TAX_TYPE_BY_CATEGORY[taxCategory] || 'standard',
          _bundleTaxLine: true,
          _parentBundleId: item.id,
        };

        if (taxCategory === 'digital') {
          synthetic.is_digital_album = true;
        }

        if (taxCategory === 'custom' && row.taxRate != null) {
          synthetic.tax_rate = row.taxRate;
        }

        expanded.push(synthetic);
      });
      continue;
    }

    expanded.push(item);
  }

  return expanded;
}

/** Sum allocated cents; falls back to line unit price when no allocations. */
export function bundleLineSubtotalCents(item) {
  if (item?.isBundle && Array.isArray(item.bundleTaxAllocations) && item.bundleTaxAllocations.length > 0) {
    const perUnit = item.bundleTaxAllocations.reduce(
      (sum, row) => sum + (Number(row.amountCents) || 0),
      0
    );
    return perUnit * (item.quantity || 1);
  }
  return getLineUnitPriceCents(item) * (item.quantity || 1);
}
